import { useState } from "react";
import type { ReactElement } from "react";
import { Chip, Hinweis } from "@kundeninfo/ui/me-design";
import type { AircallStatus, Unternehmen } from "@kundeninfo/api-client";
import { formatZeitpunkt } from "../format.js";
import "./AircallDienst.css";
import { AircallEinstellungen } from "./AircallEinstellungen.js";
import type { AircallEinstellungenClient } from "./AircallEinstellungen.js";
import { AircallLeitungen } from "./AircallLeitungen.js";
import type { AircallLeitungenClient } from "./AircallLeitungen.js";
import { AircallMengen } from "./AircallMengen.js";
import type { AircallMengenClient } from "./AircallMengen.js";
import { AircallZuordnungslaeufe, prozent } from "./AircallZuordnungslaeufe.js";

/**
 * Der AIRCALL-DIENST im Bereich „Schnittstellen“ (Spec „Aircall-Abrechnungsadapter“,
 * Stufe 1, Entscheid vom 11.09.2026) — Zustand, Kennzahlen, Leitungen, Mengen.
 *
 * **Kein eigener Menüpunkt.** Die Spec sagt es wörtlich: Der Aircall-Dienst erscheint als
 * Eintrag in der Dienste-Spalte, und sein Detail trägt den Zustand (letzter Lauf,
 * Rückstand), die Kennzahlen des Bestands (Anrufe gesamt / ungeklärt) und seit Migration
 * 051 Einstellung, Leitungen und Mengen. Diese Datei ist dieses Detail; die Zeile in der Spalte zeichnet
 * views/Schnittstellen.tsx und holt sich Beschriftung und Ampelfarbe aus `aircallZustand`
 * unten — EINE Quelle für beide Flächen.
 *
 * **Der Zustand kommt von aussen** (`status`): Gemessen wird in Schnittstellen.tsx, und
 * zwar für Spalte und Detail in EINEM Aufruf. Eine zweite Messung hier hiesse, dass Zeile
 * und Karte für denselben Augenblick verschiedene Zahlen zeigen könnten — auf einer
 * Statusseite die schlimmste Sorte Widerspruch.
 *
 * **Keine Gegennummern-Klärliste mehr** (Dirk 22.10.2026): Die Zuordnung je Gegennummer
 * ist seit E306 nur noch Information, und eine Liste je Nummer ist für eine
 * Verwaltungsseite zu viel Einzelheit — Auswertungen gehören später in ein Dashboard. Die
 * Kennzahl „Davon ungeklärt“ bleibt als Überblick; Routen und Daten bleiben bestehen.
 *
 * **Der CSS-Block heisst `ac` und gehört ALLEIN dieser Datei** (Hausregel: kein geteilter
 * Blockname über Ansichten hinweg, Lektion aus `.ka__`). Die zwei Ampel-Regeln stehen
 * darin ein zweites Mal statt als geteilte `schn`-Klasse — dieselbe Begründung, mit der
 * Schnittstellen.css die Optik des Verwaltungs-Untermenüs abschreibt statt sie zu leihen:
 * Eine Anpassung dort soll diese Karte nicht still mitverändern.
 */

/** Die Wege, die dieser Baustein braucht — die Summe der Teil-Clients, damit der Test ihn
 *  mit `vi.fn` zeichnen kann (Hausmuster: SchnittstellenClient, BereichArtikelClient). */
export type AircallClient = AircallEinstellungenClient & AircallLeitungenClient & AircallMengenClient;

export type AircallDienstProps = {
  client: AircallClient;
  /** Die gemeinsame Messung aus Schnittstellen.tsx. `null` heisst „läuft noch“ oder
   *  „gescheitert“ — welches von beidem, sagt `statusFehler`. */
  status: AircallStatus | null;
  statusFehler: boolean;
  /**
   * Nach einer Klärung an den Leitungen den ZUSTAND neu messen lassen: „ungeklärt“ und
   * „davon offen“ sind danach andere Zahlen. Ohne diesen Rückruf stünde über der Liste
   * unverändert die alte Kennzahl — und der Bearbeiter hielte die Messung für kaputt.
   */
  onStatusNeu: () => void;
  /** Für die Kürzel-Chips der Kundensuche. Ohne Angabe erscheinen sie neutral. */
  companies?: Unternehmen[];
  /** ADMIN: Schwelle ändern, Leitungen zuordnen (Migration 051, E298, E311). */
  istAdmin?: boolean;
  /** Hinweis auf die Anbindung, wenn ADMIN Aircall außerhalb des Aircall-Unternehmens sieht (E365). */
  hinweis?: string;
};

/* ------------------------------------------------------------------------------------ */
/* Der Zustand der Zeile — die EINE Ableitung für Spalte und Karte                        */
/* ------------------------------------------------------------------------------------ */

/** Die Ampelfarbe als Klassen-Endung; die Farbwerte selbst stehen in den Tokens. */
export type AircallKlasse = "ok" | "schlecht" | "warn" | "laeuft" | "aus";

export type AircallZeilenZustand = {
  label: string;
  klasse: AircallKlasse;
  /** Der Klartext für die Karte — er sagt, was IST und was zu tun wäre. */
  erklaerung: string;
};

/** Ab hier ist der Rückstand rot (Spec „Verwaltung und Sichtbarkeit“: „rot ab sieben Tagen
 *  Rückstand"). Als Konstante, weil der Test genau diese Schwelle misst. */
export const RUECKSTAND_ROT_TAGE = 7;

/** Der Name des Dienstes — in der Spalte, in der Karte und im zugänglichen Namen des
 *  Eintrags derselbe. */
export const AIRCALL_NAME = "Aircall-Anrufübernahme";

/** Die Grenze der Zuordnungsquote, wenn der Server keine nennt (Spec „Aircall: Vermittlungen", V15). */
const QUOTE_VORGABE = 90;

type Befund = NonNullable<AircallStatus["ueberwachung"]>["befunde"][number];

/** Die Klartexte der Befunde, in der Reihenfolge des Servers (FEHLER zuerst); leer, wenn keiner da ist. */
function texte(befunde: readonly Befund[]): string {
  return befunde.map((b) => b.text).join(" ");
}

/** V17: Diese FEHLER-Befunde heißen in der Zeile „Abgleich veraltet"; jeder andere FEHLER „Überwachung gestört". */
const VERALTET: readonly string[] = ["LAUF_AUSGEBLIEBEN", "STATUS_VERALTET"];

/**
 * Aus dem Status der Route wird die Zeile — die Reihenfolge der Fälle IST die Hausregel
 * „Schlechtestes oben“ (ERROR > UNKNOWN > WARN > OK), nur an einer einzelnen Zeile
 * angewandt: Trifft mehreres zu, gewinnt die schlechtere Auskunft.
 *
 * `null` + `statusFehler` heisst UNBEKANNT und ist ausdrücklich nicht „nicht
 * konfiguriert": Der Weg zur eigenen API hat nicht geantwortet: Über Aircall ist damit
 * NICHTS gesagt. Ein grauer Punkt („nicht eingerichtet“) wäre an dieser Stelle eine
 * Behauptung über ein fremdes System, die aus einem Fehler im eigenen Haus stammt.
 */
export function aircallZustand(
  status: AircallStatus | null, statusFehler: boolean,
): AircallZeilenZustand {
  if (statusFehler) {
    return {
      label: "Nicht abfragbar", klasse: "warn",
      erklaerung: "Der Stand der Anruf-Übernahme konnte nicht abgefragt werden. Über "
        + "Aircall selbst ist damit nichts gesagt — bitte in der Spalte noch einmal "
        + "aktualisieren.",
    };
  }
  if (status === null) {
    return { label: "Wird gemessen", klasse: "aus", erklaerung: "Der Stand wird geholt …" };
  }
  if (!status.configured) {
    return {
      label: "Nicht konfiguriert", klasse: "aus",
      erklaerung: "Für dieses System ist kein Aircall-Konto hinterlegt. Es wird nichts "
        + "geholt, und es gibt nichts zu klären. Einrichten kann das nur, wer an die "
        + "Konfiguration des Dienstes kommt.",
    };
  }
  // V17, Entscheid R-V1: JEDER FEHLER-Befund macht die Zeile rot (auch ZEITSTEMPEL_UNLESBAR — die Überwachung schlägt
  // geschlossen fehl, die Zeile darf dann nicht grün bleiben). LAUF_FEHLER sagt schon der gescheiterte Lauf darunter.
  // WARNUNG-Befunde (Quote, nie abgeglichen) machen sie gelb. Ohne `ueberwachung` (älterer Server) bleibt es beim Bestand.
  const befunde = status.ueberwachung?.befunde ?? [];
  const fehler = befunde.filter((b) => b.schwere === "FEHLER" && b.code !== "LAUF_FEHLER");
  const warnungen = texte(befunde.filter((b) => b.schwere === "WARNUNG"));
  if (status.rueckstandTage !== null && status.rueckstandTage >= RUECKSTAND_ROT_TAGE) {
    return {
      label: "Rückstand", klasse: "schlecht",
      erklaerung: `Der letzte erfolgreich geholte Anruf liegt ${status.rueckstandTage} Tage `
        + "zurück. Ein unbemerkter Dauerausfall ist das einzige wirklich gefährliche "
        + "Fehlerbild dieser Anbindung — bitte den Läufer `aircall-pull` und sein "
        + "Protokoll prüfen lassen.",
    };
  }
  if (fehler.length > 0) {
    return { label: fehler.some((b) => VERALTET.includes(b.code)) ? "Abgleich veraltet" : "Überwachung gestört",
             klasse: "schlecht", erklaerung: texte(fehler) };
  }
  if (status.lastRunStatus === "ERROR") {
    return {
      label: "Letzter Lauf gescheitert", klasse: "warn",
      erklaerung: "Der letzte Lauf ist abgebrochen. Der Cursor steht deshalb still, und "
        + "der nächste Lauf holt das grössere Fenster nach — verschwindet die Meldung "
        + "morgen nicht, gehört sie geprüft. Den Grund nennt das Protokoll des Läufers.",
    };
  }
  if (warnungen !== "") {
    return { label: "Nacharbeit prüfen", klasse: "warn", erklaerung: warnungen };
  }
  if (status.rueckstandTage === null) {
    return {
      label: "Noch kein Lauf", klasse: "warn",
      erklaerung: "Die Zugangsdaten stehen, aber es ist noch nie ein Lauf durchgekommen — "
        + "es liegt also noch kein Bestand vor. Solange das so bleibt, ist hier nichts zu "
        + "klären.",
    };
  }
  if (status.lastRunStatus === "RUNNING") {
    return {
      label: "Lauf läuft", klasse: "laeuft",
      erklaerung: "Gerade holt ein Lauf die Anrufe. Die Zahlen darunter sind der Stand "
        + "von eben; nach dem Lauf sagt ein Aktualisieren mehr.",
    };
  }
  return {
    label: "Aktuell", klasse: "ok",
    erklaerung: "Die Anruf-Übernahme läuft. Was hier unten offen steht, wartet auf eine "
      + "Entscheidung von Hand — alles andere ist zugeordnet oder weggeklärt.",
  };
}

const LAUF_ERGEBNIS: Record<NonNullable<AircallStatus["lastRunStatus"]>, string> = {
  OK: "erfolgreich",
  ERROR: "gescheitert",
  RUNNING: "läuft gerade",
};

/* ------------------------------------------------------------------------------------ */
/* Der Baustein                                                                           */
/* ------------------------------------------------------------------------------------ */

/** Klasse des Dienstzustands → Status-Art des Chips (me-chip `status-art`, P12 W2). */
const STATUS_ART: Record<AircallKlasse,
                         "ok" | "warnung" | "fehler" | "laeuft" | "aus"> = {
  ok: "ok", warn: "warnung", schlecht: "fehler", laeuft: "laeuft", aus: "aus",
};

export function AircallDienst(p: AircallDienstProps): ReactElement {
  const zustand = aircallZustand(p.status, p.statusFehler);
  const eingerichtet = p.status !== null && p.status.configured;
  /** Die geltende Schwelle „Nacharbeit auffällig ab" (F41) — meldet AircallEinstellungen: `undefined` solange sie
   *  lädt (die Mengen warten, damit sie nicht doppelt laden), `null`, wenn sie nicht geladen werden konnte (die
   *  Mengen laden trotzdem; der Server rechnet ohnehin mit der gespeicherten). */
  const [schwelle, setSchwelle] = useState<number | null | undefined>(undefined);

  return (
    <div className="ac">
      {p.hinweis !== undefined && <Hinweis art="info">{p.hinweis}</Hinweis>}
      <article className="ac__karte">
        <div className="ac__kopfzeile">
          {/* Übergang P12-6 (docs/ME-DESIGN.md): Ampelpunkt als eigenes span — me-ampel kennt
             nur ok/warnung/fehler (nicht laeuft/aus) und setzt ein eigenes aria-label. */}
          <span className={`ac__punkt ac__punkt--${zustand.klasse}`} aria-hidden="true" />
          <h2 className="ac__titel">{AIRCALL_NAME}</h2>
          <Chip className="ac__zustand" statusArt={STATUS_ART[zustand.klasse]}>
            {zustand.label}
          </Chip>
        </div>
        <p className="ac__erklaerung">{zustand.erklaerung}</p>
        {p.status !== null && p.status.configured && <Kennzahlen status={p.status} />}
        {/* V17: jeder Befund der Laufüberwachung als Hinweis, FEHLER zuerst (Reihenfolge des Servers). */}
        {eingerichtet && (p.status?.ueberwachung?.befunde ?? []).map((b) => (
          <Hinweis key={b.code} art={b.schwere === "FEHLER" ? "fehler" : "warnung"}>{b.text}</Hinweis>
        ))}
      </article>
      {eingerichtet && p.status?.zuordnungslaeufe !== undefined && (
        <AircallZuordnungslaeufe laeufe={p.status.zuordnungslaeufe}
          mindestProzent={p.status.mindestQuoteProzent ?? QUOTE_VORGABE} />
      )}

      {/*
        * Einstellung, Leitungen (Schlechtestes oben) und Mengen gibt es nur bei
        * eingerichtetem Dienst (Migration 051) — OHNE dass sie dafür erst leere Antworten
        * holen müssten. Ohne Zugangsdaten ist die ehrliche Auskunft die Karte darüber („kein
        * Aircall-Konto hinterlegt“); leere Listen behaupteten dort, es sei alles erledigt.
        */}
      {eingerichtet && (
        <AircallEinstellungen client={p.client} istAdmin={p.istAdmin === true} onSchwelle={setSchwelle} />
      )}
      {eingerichtet && (
        <AircallLeitungen client={p.client} istAdmin={p.istAdmin === true} onGeaendert={p.onStatusNeu}
          {...(p.companies === undefined ? {} : { companies: p.companies })} />
      )}
      {eingerichtet && schwelle !== undefined && <AircallMengen client={p.client} schwelle={schwelle} />}
    </div>
  );
}

/** Die Kennzahlen des Bestands — als Beschreibungsliste, weil es Paare aus Frage und
 *  Antwort sind und kein Fliesstext. */
function Kennzahlen({ status }: { status: AircallStatus }): ReactElement {
  const lauf = status.lastRunAt === null
    ? "noch keiner"
    : `${formatZeitpunkt(status.lastRunAt)}`
      + (status.lastRunStatus === null ? "" : ` (${LAUF_ERGEBNIS[status.lastRunStatus]})`);
  // `rueckstandTage: null` ist NICHT 0 (T4-Vertrag): „noch nie erfolgreich gelaufen“ gegen
  // „aktuell“. Zwei Auskünfte, die dieselbe Zahl 0 zusammenwürfe.
  const rueckstand = status.rueckstandTage === null
    ? "noch kein erfolgreicher Lauf"
    : status.rueckstandTage === 0
      ? "aktuell"
      : `${status.rueckstandTage} ${status.rueckstandTage === 1 ? "Tag" : "Tage"}`;

  return (
    <dl className="ac__kennzahlen">
      <div className="ac__kennzahl">
        <dt>Letzter Lauf</dt><dd>{lauf}</dd>
      </div>
      <div className="ac__kennzahl">
        <dt>Anrufe geholt bis</dt>
        <dd>{status.lastPulledUntil === null
          ? "—" : formatZeitpunkt(status.lastPulledUntil)}</dd>
      </div>
      <div className="ac__kennzahl">
        <dt>Rückstand</dt><dd>{rueckstand}</dd>
      </div>
      <div className="ac__kennzahl">
        <dt>Anrufe gesamt</dt><dd>{status.anrufeGesamt}</dd>
      </div>
      <div className="ac__kennzahl">
        <dt>Davon ungeklärt</dt><dd>{status.anrufeUngeklaert}</dd>
      </div>
      {status.leitungen !== undefined && (
        <div className="ac__kennzahl">
          <dt>Leitungen</dt><dd>{status.leitungen.gesamt}, davon offen {status.leitungen.offen}</dd>
        </div>
      )}
      {status.nacharbeit !== undefined && (
        <div className="ac__kennzahl">
          <dt>Nacharbeit abgeglichen bis</dt>
          <dd>{status.nacharbeit.statusPulledUntil === null ? "—"
            : formatZeitpunkt(status.nacharbeit.statusPulledUntil)}</dd>
        </div>
      )}
      {status.zuordnungslaeufe !== undefined && (
        <div className="ac__kennzahl">
          <dt>Zuordnungsquote (letzter Lauf)</dt>
          <dd>{prozent(status.zuordnungslaeufe[0]?.quoteProzent ?? null)}
            {` · Grenze ${status.mindestQuoteProzent ?? QUOTE_VORGABE} %`}</dd>
        </div>
      )}
    </dl>
  );
}
